import { cn } from '@/lib/utils';

/**
 * Lightweight inline markdown renderer for finding descriptions and
 * remediation steps. Uses the same hand-rolled escaper + regex passes as
 * ReportViewer so there's a single source of truth for markdown rendering
 * behavior across the app. Safe against XSS — input HTML is escaped before
 * any substitutions run.
 *
 * Intentionally *not* using react-markdown to avoid adding a dep for a
 * narrow use case. If we ever need CommonMark-faithful parsing (nested
 * blockquotes, task lists, footnotes) we can swap the implementation
 * behind this component without changing callers.
 */
export function Markdown({
  children,
  className,
}: {
  children: string | null | undefined;
  className?: string;
}) {
  if (!children) return null;
  return (
    <div
      className={cn('markdown-inline', className)}
      dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(children) }}
    />
  );
}

/**
 * Stripped-down markdown renderer focused on what agents commonly produce
 * in description / remediation fields: fenced code blocks, inline code,
 * bold/italic, bullets, numbered lists, line breaks. No headers or
 * mermaid — those belong in report-viewer, not inline descriptions.
 */
function renderInlineMarkdown(md: string): string {
  let html = md;

  // Escape HTML first — every substitution below can safely emit tags.
  html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Fenced code blocks.
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) => {
    const safe = String(code).trim();
    return `<pre class="markdown-code"><code${lang ? ` class="language-${lang}"` : ''}>${safe}</code></pre>`;
  });

  // Inline code.
  html = html.replace(/`([^`]+)`/g, '<code class="markdown-inline-code">$1</code>');

  // Bold + italic.
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Bullets + numbered lists — one li per line, merge consecutive lines
  // into a single ul afterwards. We treat `- ` and `1. ` identically.
  html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
  html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');
  html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul class="markdown-list">$1</ul>');

  // Explicit line breaks via two-space trailing.
  html = html.replace(/  $/gm, '<br>');

  // Paragraphs — wrap any remaining loose line that isn't already a block.
  html = html.replace(
    /^(?!<ul|<\/ul|<li|<pre|<code|<br|<strong|<em)(.+)$/gm,
    '<p class="markdown-p">$1</p>',
  );

  // Clean empty p tags introduced by the wrapper pass.
  html = html.replace(/<p class="markdown-p">\s*<\/p>/g, '');

  return html;
}
